import { useState } from "react";
import { TrophyIcon } from "@heroicons/react/24/solid";
import { PauseIcon, PlayIcon } from "@heroicons/react/24/outline";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import ClassNames from "embla-carousel-class-names";
import { formatMinutes } from "../../app/datetimeUtils";
import { useAppSelector } from "../../app/hooks";
import {
	PrevButton,
	NextButton,
	usePrevNextButtons,
} from "../../components/carousel/EmblaCarouselArrowButtons";
import { useAutoplay } from "../../components/carousel/EmblaCarouselAutoplay";
import EmblaCarousel from "../../components/carousel/EmblaCarousel";
import TextWithTip from "../../components/TextWithTip";
import { reportSubLevelName } from "../reports/admin/adminReportsUtil";
import {
	ReportLevel,
	TopPerformerInfo,
	TopPerformersPerLevel,
} from "../reports/admin/adminReportsApiSlice";

const initialLevelTabs = [
	{ name: "District", current: true, codeName: ReportLevel.State },
	{ name: "School", current: false, codeName: ReportLevel.District },
	{ name: "Course", current: false, codeName: ReportLevel.School },
];

interface TopPerformersCarouselProps {
	data: TopPerformersPerLevel[];
}

export default function TopPerformersCarousel(props: TopPerformersCarouselProps) {
	const { config } = useAppSelector((state) => state.site);

	// Filter tabs to only show ones that exist in props.data
	const filteredLevelTabs = initialLevelTabs
		.filter((tab) => props.data.some((d) => d.reportLevel === tab.codeName))
		.map((tab, index) => ({ ...tab, current: index === 0 }));

	const [levelTabs, setLevelTabs] = useState(filteredLevelTabs);
	const [levelTab, setLevelTab] = useState(filteredLevelTabs[0]?.codeName);

	// Initialize Embla Carousel
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
		Autoplay({ delay: 5000, playOnInit: true }),
		ClassNames(),
	]);

	function handleStatTabChange(codeName: ReportLevel): void {
		setLevelTabs(
			levelTabs.map((tab) =>
				tab.codeName === codeName ? { ...tab, current: true } : { ...tab, current: false }
			)
		);
		setLevelTab(codeName);
	}

	const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
		usePrevNextButtons(emblaApi);

	const { autoplayIsPlaying, toggleAutoplay, onAutoplayButtonClick } = useAutoplay(emblaApi);

	return (
		<div>
			<header className="pb-2 px-2 flex justify-between border-b-1 items-end">
				<div className="px-6 flex flex-wrap items-center gap-6 sm:flex-nowrap">
					<h1 className="text-base font-semibold leading-7 text-gray-900">
						Top Performers
					</h1>
					<div className="flex w-full gap-x-8 text-sm font-semibold leading-6 sm:w-auto sm:border-l sm:border-gray-200 sm:pl-6 sm:leading-7">
						{levelTabs.map((item) => (
							<button
								id={item.name}
								key={item.name}
								role="tab"
								onClick={() => handleStatTabChange(item.codeName)}
								className={`${item.current ? (config?.isUtahComposeSite ? "text-[var(--subtab-border)] usage-tab-active" : "text-primary-600 usage-tab-active") : "text-gray-700"} hover:cursor-pointer`}>
								{item.name}
							</button>
						))}
					</div>
				</div>
				<div className="grid gap-x-2 grid-cols-3">
					<PrevButton
						onClick={() => onAutoplayButtonClick(onPrevButtonClick)}
						disabled={prevBtnDisabled}
					/>
					<button className="embla__button" onClick={toggleAutoplay} type="button">
						{autoplayIsPlaying ? (
							<PauseIcon className="size-4" />
						) : (
							<PlayIcon className="size-4" />
						)}
					</button>
					<NextButton
						onClick={() => onAutoplayButtonClick(onNextButtonClick)}
						disabled={nextBtnDisabled}
					/>
				</div>
			</header>
			<EmblaCarousel
				slides={<Slides data={props.data.find((d) => d.reportLevel === levelTab)} />}
				emblaRef={emblaRef}
				emblaApi={emblaApi}
				useDefaultButtons={false}
			/>
			<hr className="mt-2"></hr>
		</div>
	);
}

interface SlidesProps {
	data?: TopPerformersPerLevel;
}

function Slides(props: SlidesProps) {
	if (!props.data) return;

	const subLevelName = reportSubLevelName(props.data.reportLevel);

	return (
		<>
			<TopPerformersSlide
				title={`Writing ${subLevelName} Top Performers`}
				data={props.data.writing}
				isCoursePerformers={props.data.reportLevel === ReportLevel.School}
			/>
			<TopPerformersSlide
				title={`Peer Review ${subLevelName} Top Performers`}
				data={props.data.reviews}
				isCoursePerformers={props.data.reportLevel === ReportLevel.School}
			/>
			<TopPerformersSlide
				title={`Lesson ${subLevelName} Top Performers`}
				data={props.data.lessons}
				isCoursePerformers={props.data.reportLevel === ReportLevel.School}
			/>
		</>
	);
}

interface SlideProps {
	title: string;
	data: TopPerformerInfo[];
	isCoursePerformers: boolean;
}

function TopPerformersSlide(props: SlideProps) {
	const isLessonSlide = props.title.startsWith("Lesson");
	return (
		<div className="embla__slide__container">
			<div className="embla__slide px-4 py-3 bg-gradient-to-b from-primary-200 to-primary-500/5">
				<span className="flex items-center font-semibold">
					<TrophyIcon className="size-6 mr-2 text-yellow-500" />
					{props.title}
				</span>
				<div
					className={`${isLessonSlide ? "grid-cols-1" : "grid-cols-3"} grid gap-x-14 mt-2 pl-8 pr-4`}>
					{props.data.map((card, index) => {
						return (
							<TopPerformersInfo
								key={index}
								info={card}
								isLessonInfo={isLessonSlide}
								isCoursePerformer={props.isCoursePerformers}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}

interface InfoProps {
	info: TopPerformerInfo;
	isLessonInfo: boolean;
	isCoursePerformer: boolean;
}

function TopPerformersInfo(props: InfoProps) {
	const displayText = props.info.winner ?? "N/A";
	const tooltipText =
		props.isCoursePerformer && props.info.winner
			? `${props.info.winner} (${props.info.teachers})`
			: props.info.winner;

	return (
		<div>
			<p className="font-medium leading-6 text-gray-600">
				{props.info.title}:{" "}
				<span className="font-semibold text-gray-900">
					{props.isLessonInfo
						? formatMinutes(Number(props.info.value))
						: (props.info.value ?? 0)}
				</span>
			</p>
			<div className="text-sm font-semibold text-primary-700">
				<TextWithTip
					uniqueName={props.info.title}
					text={displayText}
					tooltipText={tooltipText}
					forceTooltip={props.isCoursePerformer && props.info.winner != null}
					tooltipClass="max-w-96"
				/>
			</div>
		</div>
	);
}
